/**
 * Browser entry point. Mounts React into `#root` and installs the app-wide
 * providers. Everything that must wrap the whole tree belongs here.
 *
 * The order is not arbitrary:
 *
 * 1. `ColorModeProvider` builds the theme and mounts `CssBaseline`, so anything
 *    rendered inside it — including a crash screen — is already themed.
 * 2. `BrowserRouter` next, because `AuthProvider`'s consumers navigate.
 * 3. `AuthProvider` last, so `useAuth` is available to every route.
 *
 * i18next needs no provider — `react-i18next` subscribes components directly —
 * but it does need to be initialised first, which is what importing `./i18n`
 * for its side effect does.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
// Side-effect import: initialises i18next before anything renders a string.
// It must come before the first component import that calls `t()`.
import './i18n';
import { AuthProvider } from './auth/AuthProvider';
import { ColorModeProvider } from './theme/ColorModeProvider';
// The one import of the module list. Core reads it through the provider below
// and never imports it — see modules/README.md.
import { MODULES } from './modules';
import { ModulesProvider } from './modules/ModulesProvider';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Missing #root element — check index.html.');
}

createRoot(container).render(
  <StrictMode>
    <ColorModeProvider>
      {/* Real paths, not a hash. The backend's SPA fallback serves index.html
          for any non-/api path, so a deep link survives a refresh. */}
      <BrowserRouter>
        <AuthProvider>
          <ModulesProvider modules={MODULES}>
            <App />
          </ModulesProvider>
        </AuthProvider>
      </BrowserRouter>
    </ColorModeProvider>
  </StrictMode>,
);
